import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { updatePageSEO, getSiteUrl } from '../../utils/seo';

export default function RecruitPage() {
  useEffect(() => {
    const siteUrl = getSiteUrl();
    updatePageSEO({
      title: '採用情報 | おかやま美容室 - 岡山市北区の美容室',
      description: '岡山市北区の美容室おかやま美容室では、スタイリスト・アシスタントを募集しています。充実した教育制度、完全週休2日制、アットホームな職場環境。美容師としてのキャリアをスタートしたい方、経験者も歓迎します。',
      keywords: '岡山市 美容室, おかやま美容室, 採用情報, 求人, スタイリスト募集, アシスタント募集, 美容師 求人',
      canonical: `${siteUrl}/recruit`,
      ogTitle: '採用情報 | おかやま美容室 - 岡山市北区の美容室',
      ogDescription: '岡山市北区の美容室おかやま美容室では、スタイリスト・アシスタントを募集しています。',
      ogType: 'website',
      ogUrl: `${siteUrl}/recruit`,
      ogImage: 'https://public.readdy.ai/ai/img_res/b843f01c375745605eaaebabcf38109a.jpg',
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'JobPosting',
        title: 'スタイリスト・アシスタント募集',
        description: '岡山市北区の美容室おかやま美容室では、スタイリスト・アシスタントを募集しています。充実した教育制度、完全週休2日制、アットホームな職場環境。',
        datePosted: '2025-01-15',
        validThrough: '2025-12-31',
        hiringOrganization: {
          '@type': 'Organization',
          name: 'おかやま美容室株式会社',
          sameAs: siteUrl,
          logo: 'https://readdy.ai/api/search-image?query=modern-japanese-hair-salon-okayama&width=1200&height=630&seq=og001&orientation=landscape',
        },
        jobLocation: {
          '@type': 'Place',
          address: {
            '@type': 'PostalAddress',
            streetAddress: '○○町1-2-3',
            addressLocality: '岡山市北区',
            addressRegion: '岡山県',
            postalCode: '700-0000',
            addressCountry: 'JP',
          },
        },
        baseSalary: {
          '@type': 'MonetaryAmount',
          currency: 'JPY',
          value: {
            '@type': 'QuantitativeValue',
            minValue: 180000,
            maxValue: 350000,
            unitText: 'MONTH',
          },
        },
        employmentType: 'FULL_TIME',
        workHours: '9:00-18:00',
        benefits: '社会保険完備、交通費支給、制服貸与、完全週休2日制',
        qualifications: '美容師免許',
      },
    });
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="relative h-96 sm:h-[500px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/64c18942ab69a94bb8a57065a0cc7fca.jpeg"
              alt="採用情報 - 岡山市の美容室おかやま美容室"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
          </div>
          <div className="relative z-10 text-center px-4">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-wider" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              RECRUIT
            </h1>
            <p className="text-base sm:text-lg text-white tracking-widest" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              採用情報
            </p>
          </div>
        </section>

        {/* Message */}
        <section className="py-16 sm:py-20 lg:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center mb-16 sm:mb-20">
              <h2 className="text-3xl sm:text-4xl font-bold mb-6 tracking-wider" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                一緒に働きませんか？
              </h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-6">
                おかやま美容室では、お客様の美と笑顔を創造する仲間を募集しています。岡山市で長く愛される美容室として、技術力の向上とお客様への心のこもったサービスを大切にしています。
              </p>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                経験者はもちろん、美容師としてのキャリアをスタートしたい方も歓迎します。アットホームな雰囲気の中で、一緒に成長していきましょう。
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center mb-16 sm:mb-20">
              <div className="relative h-96 sm:h-[500px] rounded-lg overflow-hidden shadow-xl">
                <img
                  src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/7fdc07cff6e476c5715e3d9271386523.png"
                  alt="スタッフ育成 - 岡山市の美容室おかやま美容室"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div>
                <h3 className="text-2xl sm:text-3xl font-bold mb-6 tracking-wider" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  充実した教育制度
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-gray-900 rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                      <i className="ri-check-line text-white text-lg w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <h4 className="text-base sm:text-lg font-bold mb-2" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                        技術研修制度
                      </h4>
                      <p className="text-sm text-gray-700 leading-relaxed">
                        定期的な技術研修で、最新のカット技術やトレンドを学べます。外部講師を招いた特別研修も実施しています。
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-gray-900 rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                      <i className="ri-check-line text-white text-lg w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <h4 className="text-base sm:text-lg font-bold mb-2" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                        マンツーマン指導
                      </h4>
                      <p className="text-sm text-gray-700 leading-relaxed">
                        先輩スタイリストによる丁寧な指導で、確実にスキルアップできます。一人ひとりのペースに合わせた育成を行います。
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-gray-900 rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                      <i className="ri-check-line text-white text-lg w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <h4 className="text-base sm:text-lg font-bold mb-2" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                        資格取得支援
                      </h4>
                      <p className="text-sm text-gray-700 leading-relaxed">
                        美容師免許取得のサポートや、各種資格取得のための支援制度を整えています。
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Benefits */}
            <div className="mb-16 sm:mb-20">
              <h3 className="text-2xl sm:text-3xl font-bold mb-8 sm:mb-12 text-center tracking-wider" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                働きやすい環境
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-6 sm:p-8 rounded-lg">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white rounded-full flex items-center justify-center mb-4 sm:mb-6 shadow-md">
                    <i className="ri-calendar-check-line text-2xl sm:text-3xl text-gray-900 w-8 h-8 flex items-center justify-center"></i>
                  </div>
                  <h4 className="text-lg sm:text-xl font-bold mb-3 tracking-wider" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                    完全週休2日制
                  </h4>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    日曜日・祝日が定休日。プライベートの時間もしっかり確保できます。
                  </p>
                </div>

                <div className="bg-gradient-to-br from-rose-50 to-pink-50 p-6 sm:p-8 rounded-lg">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white rounded-full flex items-center justify-center mb-4 sm:mb-6 shadow-md">
                    <i className="ri-team-line text-2xl sm:text-3xl text-gray-900 w-8 h-8 flex items-center justify-center"></i>
                  </div>
                  <h4 className="text-lg sm:text-xl font-bold mb-3 tracking-wider" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                    アットホームな職場
                  </h4>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    少人数制で風通しの良い職場環境。スタッフ同士の仲も良く、働きやすい雰囲気です。
                  </p>
                </div>

                <div className="bg-gradient-to-br from-amber-50 to-yellow-50 p-6 sm:p-8 rounded-lg">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white rounded-full flex items-center justify-center mb-4 sm:mb-6 shadow-md">
                    <i className="ri-hand-heart-line text-2xl sm:text-3xl text-gray-900 w-8 h-8 flex items-center justify-center"></i>
                  </div>
                  <h4 className="text-lg sm:text-xl font-bold mb-3 tracking-wider" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                    充実の福利厚生
                  </h4>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    社会保険完備、交通費支給、制服貸与など、安心して働ける環境を整えています。
                  </p>
                </div>
              </div>
            </div>

            {/* Job Details */}
            <div className="mb-16">
              <h3 className="text-2xl sm:text-3xl font-bold mb-8 sm:mb-12 text-center tracking-wider" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                募集要項
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
                {/* スタイリスト */}
                <div className="bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 rounded-lg p-6 sm:p-8 shadow-sm">
                  <h4 className="text-xl sm:text-2xl font-bold mb-6 tracking-wider text-center" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                    スタイリスト
                  </h4>
                  <dl className="space-y-4">
                    <div className="pb-4 border-b border-gray-300">
                      <dt className="text-sm font-semibold text-gray-900 mb-1">応募資格</dt>
                      <dd className="text-sm text-gray-700">美容師免許をお持ちの方（実務経験3年以上歓迎）</dd>
                    </div>
                    <div className="pb-4 border-b border-gray-300">
                      <dt className="text-sm font-semibold text-gray-900 mb-1">給与</dt>
                      <dd className="text-sm text-gray-700">月給25万円〜35万円（経験・能力により優遇）</dd>
                    </div>
                    <div className="pb-4 border-b border-gray-300">
                      <dt className="text-sm font-semibold text-gray-900 mb-1">勤務時間</dt>
                      <dd className="text-sm text-gray-700">9:00〜18:00（休憩1時間）</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-semibold text-gray-900 mb-1">休日</dt>
                      <dd className="text-sm text-gray-700">完全週休2日制（日曜日・祝日）、夏季休暇、年末年始休暇</dd>
                    </div>
                  </dl>
                </div>

                {/* アシスタント */}
                <div className="bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 rounded-lg p-6 sm:p-8 shadow-sm">
                  <h4 className="text-xl sm:text-2xl font-bold mb-6 tracking-wider text-center" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                    アシスタント
                  </h4>
                  <dl className="space-y-4">
                    <div className="pb-4 border-b border-gray-300">
                      <dt className="text-sm font-semibold text-gray-900 mb-1">応募資格</dt>
                      <dd className="text-sm text-gray-700">美容師免許をお持ちの方（未経験者歓迎）</dd>
                    </div>
                    <div className="pb-4 border-b border-gray-300">
                      <dt className="text-sm font-semibold text-gray-900 mb-1">給与</dt>
                      <dd className="text-sm text-gray-700">月給18万円〜22万円</dd>
                    </div>
                    <div className="pb-4 border-b border-gray-300">
                      <dt className="text-sm font-semibold text-gray-900 mb-1">勤務時間</dt>
                      <dd className="text-sm text-gray-700">9:00〜18:00（休憩1時間）</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-semibold text-gray-900 mb-1">休日</dt>
                      <dd className="text-sm text-gray-700">完全週休2日制（日曜日・祝日）、夏季休暇、年末年始休暇</dd>
                    </div>
                  </dl>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="text-center">
              <h3 className="text-2xl sm:text-3xl font-bold mb-6 tracking-wider" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                応募・お問い合わせ
              </h3>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-8 max-w-2xl mx-auto">
                ご応募やご質問は、お電話またはお問い合わせフォームよりお気軽にご連絡ください。<br />
                まずは見学だけでも大歓迎です。
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="tel:086-012-3456"
                  className="inline-flex items-center px-8 py-4 bg-gray-900 text-white text-sm font-semibold rounded-full hover:bg-gray-800 transition-all duration-300 whitespace-nowrap cursor-pointer justify-center"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  <i className="ri-phone-line mr-2 w-5 h-5 flex items-center justify-center"></i>
                  086-012-3456
                </a>
                <Link
                  to="/contact"
                  className="inline-flex items-center px-8 py-4 bg-transparent border-2 border-gray-900 text-gray-900 text-sm font-semibold rounded-full hover:bg-gray-900 hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer justify-center"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  <i className="ri-mail-line mr-2 w-5 h-5 flex items-center justify-center"></i>
                  お問い合わせ
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
